/**
 * "Understanding your plan" / "Explain this" — the full plain-words explanation, on its own surface.
 *
 * WHY A MODAL AND NOT AN EXPANDER. The plan summary panel carries one or two sentences and must
 * always fit the screen; the full recap runs to several paragraphs plus a before→after row and a
 * reason for each of pension, cashflow and estate. An expander that long would put the scrolling
 * back inside the panel, which is the problem the panel exists to remove (owner, 2026-08-26:
 * "im having to scroll both up and down the screen to see what the hell is going on").
 *
 * Two headings (UX review, 17 Oct 2026): the modules keep "Understanding your plan"; the aged care
 * engine passes an eyebrow ("Explain this"), the plan's own words as the title, and an intro line.
 * The mock-up's eyebrow said "the way an adviser would say it"; the owner struck it on 18 Oct 2026
 * because the client-facing app must never speak of advisers. Navy, not the teal gradient, so it
 * reads as the same page and not a banner. The chrome itself is DialogShell, shared by every pop-over.
 *
 * This component is chrome ONLY. Everything inside it is the recap, unchanged and still the single
 * place that reconciles the plan in words.
 */
import React from 'react';
import DialogShell from '../../components/ui/DialogShell.tsx';

export interface PlanExplanationModalProps {
    /** What is being explained, echoed under the heading so the reader keeps the thread. */
    strategyName?: string | null;
    onClose: () => void;
    children: React.ReactNode;
    /**
     * An action under the explanation — the "Deep dive" into the year-by-year figures lives here
     * now rather than as a fourth button on the card (owner, 2 Oct 2026: three buttons, not five).
     */
    footer?: React.ReactNode;
    /** A small chip above the title — "Explain this". Never a note to the builder (owner, 18 Oct 2026). */
    eyebrow?: string | null;
    /** The heading. Default "Understanding your plan"; the aged care engine passes the plan's words. */
    title?: string;
    /** One line under the heading saying how to read what follows. */
    intro?: React.ReactNode;
}

const PlanExplanationModal = ({ strategyName, onClose, children, footer, eyebrow = null, title = 'Understanding your plan', intro = null }: PlanExplanationModalProps) => (
    <DialogShell
        titleId="plan-explanation-heading"
        testId="plan-explanation"
        eyebrow={eyebrow ?? undefined}
        title={title}
        intro={intro ?? (strategyName && strategyName !== title ? strategyName : null)}
        onClose={onClose}
        footer={footer}
    >
        <div className="[&>*]:mt-0">{children}</div>
    </DialogShell>
);

export default PlanExplanationModal;
